import { Link } from "react-router-dom";
import { FileText, FileEdit, ClipboardList, ArrowUpRight, Sparkles } from "lucide-react";

const CARDS = [
    {
        key: "fill",
        title: "Fill My Form",
        desc: "Professional online form filling service — done accurately, on time, every time.",
        icon: FileText,
        accent: "from-[#3BC7FF] to-[#00D4FF]",
        to: "/services",
    },
    {
        key: "modify",
        title: "Modify My Form",
        desc: "Correct or update previously submitted forms with expert assistance.",
        icon: FileEdit,
        accent: "from-[#7B61FF] to-[#3BC7FF]",
        to: "/services",
    },
    {
        key: "registers",
        title: "Registers",
        desc: "One-stop access to government and private registration services.",
        icon: ClipboardList,
        accent: "from-[#00D4FF] to-[#7B61FF]",
        to: "/services",
    },
];

export default function HomePage() {
    return (
        <section
            className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-16"
            data-testid="home-page"
        >
            <div className="absolute inset-x-0 top-0 -z-10 grid-overlay h-[280px]" aria-hidden="true" />

            {/* Hero */}
            <div className="max-w-3xl">
                <span
                    className="inline-flex items-center gap-2 rounded-full px-3 py-1 glass-card text-[11px] uppercase tracking-[0.24em] text-muted-foreground"
                    data-testid="home-eyebrow"
                >
                    <Sparkles className="h-3 w-3 text-primary" />
                    Trusted online services
                </span>
                <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05]">
                    Fast, reliable, <span className="text-gradient-brand">digital services</span>
                    <br className="hidden sm:block" /> for individuals &amp; businesses.
                </h1>
                <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl">
                    Smart Solutions helps you complete forms, register with authorities and
                    move money — with a modern, secure, human-supported experience.
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                    <Link
                        to="/services"
                        data-testid="home-cta-services"
                        className="inline-flex items-center gap-2 h-11 px-5 rounded-full btn-gradient ripple text-white text-sm font-semibold"
                    >
                        Explore services <ArrowUpRight className="h-4 w-4" />
                    </Link>
                    <Link
                        to="/contact"
                        data-testid="home-cta-contact"
                        className="inline-flex items-center gap-2 h-11 px-5 rounded-full glass-card hover-lift text-sm font-semibold"
                    >
                        Talk to us
                    </Link>
                </div>
            </div>

            {/* Cards */}
            <div
                className="mt-10 lg:mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6"
                data-testid="home-service-cards"
            >
                {CARDS.map((c, i) => {
                    const Icon = c.icon;
                    return (
                        <Link
                            key={c.key}
                            to={c.to}
                            data-testid={`home-card-${c.key}`}
                            className="group relative glass-card rounded-3xl p-6 lg:p-7 hover-lift overflow-hidden fade-in-up"
                            style={{ animationDelay: `${120 * i}ms` }}
                        >
                            {/* corner glow */}
                            <div
                                className={`pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-gradient-to-br ${c.accent} opacity-25 blur-3xl group-hover:opacity-50 transition`}
                                aria-hidden="true"
                            />
                            <div className={`relative h-14 w-14 rounded-2xl bg-gradient-to-br ${c.accent} p-[1.5px]`}>
                                <div className="h-full w-full rounded-[14px] bg-background/80 backdrop-blur flex items-center justify-center">
                                    <Icon className="h-6 w-6 text-foreground" strokeWidth={1.8} />
                                </div>
                            </div>
                            <h3 className="relative mt-5 text-xl sm:text-2xl font-display font-semibold tracking-tight">
                                {c.title}
                            </h3>
                            <p className="relative mt-2 text-sm text-muted-foreground leading-relaxed">
                                {c.desc}
                            </p>
                            <div className="relative mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                                Get started
                                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </div>
                        </Link>
                    );
                })}
            </div>
        </section>
    );
}
